/**
 * GraphsColumnConfig Component
 * Allows users to select which numeric fields to display on the chart
 */

import React, { useState, useCallback } from 'react';
import { filterNumericFields } from './utils';

/**
 * @param {Object} props
 * @param {Array} props.availableFields - Array of available field configurations
 * @param {Array} props.selectedFields - Array of currently selected field keys
 * @param {Function} props.onFieldsChange - Callback when field selection changes
 * @param {Function} props.onClose - Callback to close the config panel
 */
export const GraphsColumnConfig = ({
  availableFields = [],
  selectedFields = [],
  onFieldsChange,
  onClose,
}) => {
  // Local state for pending changes
  const [localSelected, setLocalSelected] = useState(selectedFields);

  // Filter to only show numeric fields
  const numericFields = filterNumericFields(availableFields);

  // Handle checkbox change
  const handleCheckboxChange = useCallback((fieldKey, checked) => {
    setLocalSelected((prev) => {
      if (checked) {
        return [...prev, fieldKey];
      }
      return prev.filter((key) => key !== fieldKey);
    });
  }, []);

  // Apply changes
  const handleApply = useCallback(() => {
    onFieldsChange && onFieldsChange(localSelected);
    onClose && onClose();
  }, [localSelected, onFieldsChange, onClose]);

  // Cancel changes
  const handleCancel = useCallback(() => {
    setLocalSelected(selectedFields);
    onClose && onClose();
  }, [selectedFields, onClose]);

  // Select all
  const handleSelectAll = useCallback(() => {
    setLocalSelected(numericFields.map((f) => f.key));
  }, [numericFields]);

  // Clear all
  const handleClearAll = useCallback(() => {
    setLocalSelected([]);
  }, []);

  return (
    <div className="graphs-column-config">
      <div className="graphs-column-config__header">
        <h4>Select Fields to Display</h4>
      </div>

      <div className="graphs-column-config__actions">
        <button
          type="button"
          className="btn-link"
          onClick={handleSelectAll}
        >
          Select All
        </button>
        <button
          type="button"
          className="btn-link"
          onClick={handleClearAll}
        >
          Clear All
        </button>
      </div>

      <div className="graphs-column-config__list">
        {numericFields.map((field) => {
          const isChecked = localSelected.includes(field.key);
          return (
            <label
              key={field.key}
              className="graphs-column-config__item"
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => handleCheckboxChange(field.key, e.target.checked)}
              />
              <span
                className="graphs-column-config__color"
                style={{ backgroundColor: field.color }}
              />
              <span className="graphs-column-config__label">
                {field.label}
                {field.unit && <span className="unit">({field.unit})</span>}
              </span>
            </label>
          );
        })}
      </div>

      <div className="graphs-column-config__footer">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default GraphsColumnConfig;
